import type {StoryblokStory} from 'storyblok-generate-ts';
import type {AsyncDataOptions, NuxtError, AsyncData} from 'nuxt/app';
import type {ISbStoryParams, ISbStoriesParams} from 'storyblok-js-client';
import {getCommonStoryParams} from '~/storyblok/api/options';
import type {ErrorWithApplicationErrorID} from '~/composables/useShowError';
import {abortController} from '~/utils';

type StoryError = NuxtError<null> | NuxtError<ErrorWithApplicationErrorID> | null;

/**
 * Fetches a single story or a list of stories from Storyblok API using `useAsyncData`.
 *
 * @param fullSlugOrKey - The identifier for the fetch. If it starts with `_globus-cz`,it is treated as a slug and encoded.
 * Otherwise, it is treated as a generic key. In both cases, it is used as the `useAsyncData` key.
 * @param sbParams - Optional Storyblok API parameters to customize the query.
 * @param asyncDataOptions - Optional Nuxt `useAsyncData` options.
 */
export default function useSingleStoryFetch<
	T = any,
	ResT = StoryblokStory<T> | null,
>(
	fullSlugOrKey: MaybeRefOrGetter<string>,
	sbParams?: ISbStoryParams,
	asyncDataOptions?: AsyncDataOptions<StoryblokStory<T> | null, ResT>,
): AsyncData<ResT, StoryError>;

export default function useSingleStoryFetch<
	T = any,
	ResT = StoryblokStory<T>[] | null,
>(
	fullSlugOrKey: MaybeRefOrGetter<string>,
	sbParams?: ISbStoriesParams,
	asyncDataOptions?: AsyncDataOptions<StoryblokStory<T>[] | null, ResT>,
): AsyncData<ResT, StoryError>;

export default function useSingleStoryFetch(
	fullSlugOrKey: MaybeRefOrGetter<string>,
	sbParams: ISbStoryParams | ISbStoriesParams = {},
	asyncDataOptions?: any,
) {
	const {$storyblokApi} = useNuxtApp();
	let abort: AbortController['abort'] | null = null;

	return useAsyncData(
		() => `storyblok-story-${toValue(fullSlugOrKey)}`,
		async () => {
			abort?.();
			const {signal, abort: newAbort} = abortController();
			if (import.meta.client) {
				abort = newAbort;
			}

			let encodedSlug = '';

			if (toValue(fullSlugOrKey).startsWith('_globus-cz')) {
				encodedSlug = encodeURIComponent(encodeURIComponent(toValue(fullSlugOrKey)));
			}

			const {data} = await $storyblokApi.get(`stories/${encodedSlug}`, {...getCommonStoryParams(), ...sbParams}, {signal});
			return (data?.story ?? data?.stories ?? null) as StoryblokStory<any> | StoryblokStory<any>[] | null;
		},
		asyncDataOptions,
	) as AsyncData<any, StoryError>;
}
